import {
  IBallConfig, IPhysicsConfig,
  IAssetsConfig, IRotation, IVector2
} from "../types/game-config-type";
import { GameConfig } from "../config/game-config";
import { table } from "./table";
import { BallTypeEnum } from "../types/ball-type-enum";
import { Vector2 } from "../math/vector2";
import { Assets } from "./resources";
import { Orientation } from "../math/orientation";

const physicsConfig: IPhysicsConfig = GameConfig.physics;
const sprites: IAssetsConfig = GameConfig.sprites;
const ballConfig: IBallConfig = GameConfig.ball;

/**
 * This class represents the ball.
 */
export class Ball {
  private sprite: HTMLImageElement;
  private ballVelocity: Vector2 = Vector2.zero;
  private isMoving: boolean = false;
  private isVisible: boolean = true;

  private isShaking: boolean = false;
  private isFalling: boolean = false;
  private inPocket: boolean = false;
  private shakeIndex: number = -1;
  private shakeIndexesCount: number = 30;
  private shakeVelocity: number = 2;
  private fallIndex: number = 0;
  
  private ballOrientation: Orientation; // ball orientation in 3D
  private ballLabelOrient: Orientation; // ball label position in 3D
  private ballRotation: IRotation = { x: 0, y: 0, z: 0 }; // ball rotation for cue ball non-default hit position
  
  /**
   * This is the ball number for solid and striped balls.
   * It is used in the sprites to get the correct image.
   */
  private ballNumber: number; 

  private ballType: BallTypeEnum; // ball type
  private points: number = 1; // this is the default ball points for the Snooker game balls
  private icon: string = 'red.svg'; // this is the default ball icon for the Snooker game

  public get position(): Vector2 {
    return Vector2.copy(this.thePosition);
  }

  public set position(value: Vector2) {
    this.thePosition = value;
  }

  public get nextPosition(): Vector2 {
    return this.position.add(
      this.ballVelocity.mult(1 - physicsConfig.friction)
    );
  }

  public get velocity(): Vector2 {
    return Vector2.copy(this.ballVelocity);
  }

  public set velocity(value: Vector2) {
    this.isMoving = value.length > 0 ? true : false;
    this.ballVelocity = value;
  }

  public set rotation(value: IRotation) {
    this.ballRotation = value;
  }

  public get rotation(): IRotation {
    return this.ballRotation;
  }

  public get ballIcon(): string {
    return this.icon;
  }

  public set ballIcon(value: string) {
    this.icon = value;
  }

  public get ballPoints(): number {
    return this.points;
  }

  public set ballPoints(value: number) {
    this.points = value;
  }

  public get orientaion(): Orientation {
    return this.ballOrientation;
  }

  public set orientaion(value: Orientation) {
    this.ballOrientation = value;
  }

  public get onTable(): boolean {
    if (this.inPocket == true) return false;
    else if (this.visible == false) return false;
    else return true;
  }

  public get moving(): boolean {
    return this.isMoving;
  }

  public get type(): BallTypeEnum {
    return this.ballType;
  }

  public get number(): number {
    return this.ballNumber;
  }

  public get visible(): boolean {
    return this.isVisible;
  }

  constructor(private thePosition: Vector2, theType: BallTypeEnum, theNumber: number) {
    this.ballType = theType;
    this.ballNumber = theNumber;
    this.ballOrientation = new Orientation();
    this.ballLabelOrient = new Orientation(0, Math.PI / 2);

    // rotate ball randomly on creation
    const rotX = Math.random() * 2 * Math.PI - Math.PI;
    const rotY = Math.random() * 2 * Math.PI - Math.PI;
    this.ballOrientation.rotateX(rotX);
    this.ballOrientation.rotateY(rotY);
    this.ballLabelOrient.rotateX(rotX);
    this.ballLabelOrient.rotateY(rotY);

    this.resolveSprite();
  }

  private resolveSprite(): void {
    if (this.ballNumber > 8) {
      this.sprite = Assets.getSprite(sprites.paths['ball' + this.ballNumber]);
    } else {
      this.sprite = Assets.getSprite(sprites.paths.solidBalls);
    }
  }

  /**
   * This is executed when the cue ball is hit.
   * 
   * @param power - the cue stick hit strength
   * @param angle - the angle the ball is hit
   * @param hitPosition - the position on the ball where it is hit
   */
  public shoot(power: number, angle: number, hitPosition: Vector2): void {

    // set velocity based on hit power and angle
    this.ballVelocity = new Vector2(
      power * Math.cos(angle),
      power * Math.sin(angle)
    );

    // set rotation based on power and hit position
    const rotationPowerX = power * hitPosition.y * GameConfig.physics.powerToRotationRatio
    const rotationPowerY = power * hitPosition.x * GameConfig.physics.powerToRotationRatio
    this.ballRotation = {
      x: rotationPowerX,
      y: rotationPowerY,
      z: 0,
    };

    this.isMoving = true;
  }

  public update(): void {
    if (this.isMoving) { // this is executed when the ball is moving
      // update ball velocity (add friction)
      this.ballVelocity.multBy(1 - physicsConfig.friction);

      // update ball position
      this.thePosition.addTo(this.ballVelocity);

      // rotation friction to rolling (velocity) friction ratio & counter rotating friction ratio
      const rotFriction = GameConfig.physics.rotationFriction;
      const counterRotFriction = GameConfig.physics.counterRotationFriction;
 
      // update ball rotation (if the ball is counter rotating - add additional friction)
      this.ballRotation.x *=
        Math.sign(this.ballVelocity.y) === Math.sign(this.ballRotation.x)
          ? 1 - rotFriction
          : 1 - counterRotFriction;
      this.ballRotation.y *=
        Math.sign(this.ballVelocity.x) === Math.sign(this.ballRotation.y)
          ? 1 - rotFriction
          : 1 - counterRotFriction;
      this.ballRotation.z *= 1 - rotFriction;
      
      // summing rotation from rolling (velocity) and self rotation
      const angleX = (this.ballVelocity.y * 2) / GameConfig.ball.diameter + this.ballRotation.x;
      const angleY = (this.ballVelocity.x * 2) / GameConfig.ball.diameter + this.ballRotation.y;
      const angleZ = this.ballRotation.z;

      // rotate ball in accordance with rotations
      this.ballOrientation.rotate({ x: angleX, y: angleY, z: angleZ });
      this.ballLabelOrient.rotate({ x: angleX, y: angleY, z: angleZ });

      // Don't stop the ball if it's falling in the potted balls bar.
      // The potted balls stop is perfomed in the function "stopInPocket".
      // Without this condition the ball might get stuck in the potted balls bar.
      if (!this.isFalling) {
        // stop the ball if velocity is too low (or add velocity due to rotation)
        this.stop();
      }
    }
    if (this.isShaking) {
      this.updateShakeInPocket();
    }
    if (this.isFalling) {
      this.updateFallInPocket();
    }
  }

  private updateShakeInPocket(): void {
    ++this.shakeIndex;

    const stage = this.shakeIndex / this.shakeIndexesCount; // get the percent of animation done
    const angle = stage * Math.PI * 2; // get the angle from 0 degrees to 360 degrees

    // multiply the velocity
    this.ballVelocity = new Vector2(Math.cos(angle), Math.sin(angle)).multBy(
      this.shakeVelocity
    );

    // end shaking if this is the last frame of shake
    if (this.shakeIndex >= this.shakeIndexesCount) {
      this.isShaking = false;
      this.shakeIndex = 0; // reset the shake index incase the ball is respotted
      this.hide();
    }
  }

  /**
   * This animates the ball when it hits a pocket.
   * This starts the "shaking" animation before the ball falls into the potted balls bar.
   *
   * @param pocket - the location of the pocket
   */
  public shake(pocket: IVector2): void {
    this.isShaking = true;
    this.isMoving = true;
    this.ballVelocity = new Vector2(0, 0);
    this.position = new Vector2(pocket.x, pocket.y);
    this.ballRotation = { x: 1, y: 1, z: 1 };
  }

  /**
   * This starts the animation when the ball falls into the potted balls bar.
   *
   * @param index - the index of the ball on the potted balls bar
   */
  public fall(index: number): void {
    this.isShaking = false;
    this.isFalling = true;
    this.isMoving = true;
    this.isVisible = true;
    this.inPocket = true;
    this.fallIndex = index;

    const { x, y } = GameConfig.pottedBallsSpawn;
    this.position = new Vector2(x, y); // set ball position to center of pocket
    this.ballRotation = { x: 1, y: 1, z: 1 };
    this.ballVelocity = new Vector2(-3, 0);
  }

  private updateFallInPocket(): void {
    // If we touched the left side of potted balls bar, start going downwards.
    if (this.position.x <= GameConfig.pottedBallsLeftEdge || this.ballVelocity == new Vector2(-3, 0)) {
      this.ballVelocity = new Vector2(0, +5);
    }

    // If the ball has touched the bottom side of potted balls bar, stop falling.
    if (this.position.y >= GameConfig.pottedBallsBottomEdge -
        GameConfig.ball.diameter / 2 -
        GameConfig.ball.diameter * this.fallIndex) {
      this.stopInPocket();
    }
  }

  /**
   * Show the ball.
   * 
   * @param thePosition - the position of the ball on the table
   */
  public show(thePosition: Vector2): void {
    this.position = thePosition;
    this.ballRotation = { x: 0, y: 0, z: 0 };
    this.isVisible = true;
    this.inPocket = false;
  }
  
  private hide(): void {
    this.ballRotation = { x: 0, y: 0, z: 0 };
    this.isVisible = false;
  }

  private stopInPocket(): void {
    this.isFalling = false;
    this.velocity = Vector2.zero;
    this.isMoving = false;
    this.ballRotation.x = 0;
    this.ballRotation.y = 0;
    this.ballRotation.z = 0;
  }

  /**
   * This is executed when the ball stops.
   */
  private stop(): void {
    const stopVelocity = 0.001;

    // stop rotation if it is too slow
    if (Math.abs(this.ballRotation.x) < stopVelocity) {
      this.ballRotation.x = 0;
    }
    if (Math.abs(this.ballRotation.y) < stopVelocity) {
      this.ballRotation.y = 0;
    }
    if (Math.abs(this.ballRotation.z) < stopVelocity) {
      this.ballRotation.z = 0;
    }

    if (this.ballVelocity.length < ballConfig.minVelocityLength) {
      if (this.ballRotation.x || this.ballRotation.y) {
        // if ball has rotation convert it into speed
        const xVelocity = ((this.ballRotation.y * GameConfig.ball.diameter) / 2) * 1.2;
        const yVelocity = ((this.ballRotation.x * GameConfig.ball.diameter) / 2) * 1.2;
        this.ballVelocity = new Vector2(xVelocity, yVelocity);
        this.ballRotation.x = 0;
        this.ballRotation.y = 0;
      } else {
        // if the ball doesn't have rotation
        this.velocity = Vector2.zero;
        this.isMoving = false;
        this.ballRotation.x = 0;
        this.ballRotation.y = 0;
      }
    }
  }

  public draw(): void {
    if (this.isVisible) {
      table.drawBall(
        this.sprite,
        this.thePosition,
        this.ballOrientation,
        this.ballLabelOrient,
        this.ballNumber,
        // the shadow is currently removed 
        // this is because if issues with high resoultion screens and performance
        // to enable it change to this value to !this.inPocket
        false,
      );
    }
  }
}
